import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {CRLApi, X509CRLDetails} from "components/src/api/admin/generated";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    Bin as DeleteIcon,
    CardShield as CRLIcon,
    DatabaseExport as ExportIcon,
    Import as ImportCRLIcon,
    Refresh as RefreshIcon
} from "iconoir-react";
import {useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useNavigate} from "react-router-dom";
import {CRLExportDialog} from "./CRLExportDialog";
import {CRLImportDialog} from "./CRLImportDialog";
import {CRLRow} from "./CRLRow";

/*
 * REST api for accessing the back-end
 */
const crlApi = new CRLApi(ApiConfig.config());

/**
 * The CRLs component is responsible for managing and displaying Certificate Revocation Lists (CRLs).
 * It provides functionality such as refreshing the CRL store, exporting and importing CRLs,
 * and deleting selected CRLs. It also includes pagination and the ability to select multiple CRLs.
 *
 * @module CRLs
 */
export const CRLs = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The loaded CRLs
     */
    const [crls, setCrls] = useState<X509CRLDetails[]>([]);
    /**
     * The total number of CRLs
     */
    const [crlCount, setCRLCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of CRLs to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected CRLs
     */
    const [selectedCRLs, setSelectedCRLs] = useState<X509CRLDetails[]>([]);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the export CRLs dialog will be shown
     */
    const [showCRLExportDialog, setShowCRLExportDialog] = useState<boolean>(false);
    /**
     * If true, the import CRLs dialog will be shown
     */
    const [showCRLImportDialog, setShowCRLImportDialog] = useState<boolean>(false);
    /**
     * If true, the CRLs will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() => {
        logger.debug("Getting CRL count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        crlApi.cRLControllerGetCRLsCount()
            .then(response => {
                setCRLCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting CRL count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        forceReload,
        t
    ]);

    useEffect(() => {
        logger.debug("Getting CRLs")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setSelectedCRLs([]);
        setForceReload(false);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        crlApi.cRLControllerGetCRLs((currentPage - 1) * rowsPerPage, rowsPerPage).then(response => {
            setCrls(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting CRLs", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        forceReload,
        currentPage,
        rowsPerPage,
        t
    ]);

    const deleteSelected = () => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        crlApi.cRLControllerDeleteCRLs(selectedCRLs.map(crl => crl.thumbprint))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting CRLs", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowDeleteConfirm(false);
            setSelectedCRLs([]);
        })
    }

    /**
     * Called when a CRL is selected (i.e., when the checkbox is checked)
     */
    const onSelectCRL = (checked: boolean, crl: X509CRLDetails) =>
    {
        setSelectedCRLs(checked ?
            [...selectedCRLs, crl] :
            selectedCRLs.filter(e => e.thumbprint !== crl.thumbprint)
        );
    }

    /**
     * Inverts the selection of CRLs by updating the list of selected CRLs.
     * Each previously unselected CRL will be selected, and each previously
     * selected CRL will be unselected.
     */
    const invertSelection = () => {
        setSelectedCRLs([...crls].filter(crl => !selectedCRLs.includes(crl)));
    }

    /**
     * Force the back-end to load new CRLs
     */
    const refreshCRLStore = () => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        crlApi.cRLControllerRefreshCRLStore().then(() => {
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error refreshing CRL store", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    /**
     * Is called when the ExportCRLsDialog is closed
     */
    const onExportCRLsDialogClose = (closeCause: DialogCloseCause) => {
        setShowCRLExportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            // clear the selected CRLs after export
            setSelectedCRLs([]);
        }
    }
    /**
     * Is called when the ImportCRLsDialog is closed
     */
    const onImportCRLsDialogClose = (closeCause: DialogCloseCause) => {
        setShowCRLImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("crls.are-you-sure-crls")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showCRLExportDialog &&
                <CRLExportDialog crls={selectedCRLs} onClose={onExportCRLsDialogClose}/>}
            {showCRLImportDialog && <CRLImportDialog onClose={onImportCRLsDialogClose}/>}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<CRLIcon />} title={t("crls.title")} onClickReload={() => setForceReload(true)}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowCRLImportDialog(true)}>
                                        <ImportCRLIcon className="text-success" /> {t("crls.import-crls")}
                                    </Dropdown.Item>
                                    <Dropdown.Divider/>
                                    <Dropdown.Item onClick={() => refreshCRLStore()}>
                                        <RefreshIcon className="text-success" /> {t("crls.refresh-crl-store")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    {selectedCRLs.length === 0 ?
                                        <>
                                            <th className="th-w-min">
                                                {/* Add icon to first column which will invert selection when clicked */}
                                                <InvertSelection invertSelectionEvent={invertSelection}/>
                                            </th>
                                            <th>{t("crls.issuer")} </th>
                                            <th>{t("crls.next-update")} </th>
                                            <th>{t("crls.this-update")} </th>
                                            <th>{t("crls.version")} </th>
                                            <th>{t("crls.crl-number")} </th>
                                            <th>{t("crls.delta-indicator")} </th>
                                            <th>{t("crls.is-delta-crl")} </th>
                                            <th>{t("crls.thumbprint-sha512")} </th>
                                            <th>{t("crls.thumbprint-sha1")} </th>
                                            <th>{t("crls.signature-algorithm")} </th>
                                            <th>{t("crls.validation-error")} </th>
                                        </>
                                        :
                                        <th colSpan={12}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedCRLs([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedCRLs.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("crls.delete-selected")}
                                                    </Nav.Link>

                                                    <Nav.Link  onClick={() => setShowCRLExportDialog(true)}>
                                                        <ExportIcon className="text-success" /> {t("crls.export-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>

                                <tbody>
                                {crls.map((crl: X509CRLDetails) => (
                                    <CRLRow key={crl.thumbprint} crl={crl}>
                                        {/* Add selection checkbox to first column */}
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedCRLs.find((scrl) =>  scrl.thumbprint === crl.thumbprint) !== undefined}
                                            onChange={(e) => onSelectCRL(e.target.checked, crl)}
                                            inline
                                        />
                                    </CRLRow>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={crlCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
